<template>
  <div
    class="relative box-border w-screen h-screen flex overflow-hidden"
    :class="{ 'flex-row-reverse': !isLeft }"
  >
    <!-- Loading overlay -->
    <Transition
      mode="out-in"
      enter-active-class="transition"
      enter-from-class="opacity-0"
      leave-active-class="transition"
      leave-to-class="opacity-0"
    >
      <div v-if="preload" class="transition-gradient flex items-center justify-center">
        <UiLoader size="80px" />
      </div>
    </Transition>

    <!-- Background/Carousel Section -->
    <div class="absolute w-full h-full overflow-hidden">
      <template v-if="systemStore.clientSettings.loadedOnce">
        <LoginCarousel v-if="carousels?.length" :carousels="carousels" />
        <LoginBackground v-else :bg-image="bgImage || ''" :is-left="isLeft" :is-center="isCenter" />
      </template>
    </div>

    <!-- Content Wrapper -->
    <div class="z-[1] w-full h-full max-h-screen overflow-auto flex items-center flex-wrap">
      <!-- Main Content -->
      <div class="w-full relative box-border h-full flex flex-col items-center justify-between text-[#212121] dark:text-[#FDFDFD] overflow-y-auto">
        <!-- Header Actions -->
        <div 
          class="z-[3] absolute flex flex-row-reverse gap-2 top-4 items-center justify-center rounded-2xl glass-bg" 
          :class="!isLeft && !mobile && !isCenter ? 'left-4' : 'right-4'"
        >
          <!-- Theme Toggle -->
          <UiButton 
            :disabled="blockTheme" 
            v-tooltip 
            :title="blockTheme ? $t('mainContent.customizations.theme.blockTheme', { theme: $t(systemStore.isDark ? 'labels.dark' : 'labels.light')?.toLowerCase() }) : ''"
            type="ghost" 
            round 
            class="p-2 !rounded-full" 
            @click="systemStore.toggleTheme()"
          >
            <transition
              mode="out-in"
              enter-active-class="transition ease-out duration-150 transform"
              enter-from-class="opacity-50 rotate-90"
              leave-active-class="ease-in duration-200"
              leave-to-class="opacity-50 -rotate-90"
            >
              <UiIcon v-if="!systemStore.isDark" icon="moon" :size="21" />
              <UiIcon v-else icon="sun-high" :size="21" />
            </transition>
          </UiButton>

          <!-- Language Selector -->
          <UiButton type="ghost" round class="h-10 w-10">
            <UiLangSelector size="md" />
          </UiButton>

          <!-- Certificate Validation -->
          <UiButton
            type="ghost"
            to="/cert"
            class="p-2 !rounded-full"
            round
            icon="certificate"
            v-tooltip="{ placement: 'bottom' }"
            :title="$t('login.certificateValidation')"
          />
        </div>

        <!-- Main Form Container -->
        <div
          class="relative w-full h-full grid grid-col items-center"
          :class="{ 'md:grid-cols-2 lg:grid-cols-3': !isCenter }"
        >
          <div
            class="relative h-full col-start-1"
            :class="!isCenter ? ( !isLeft ? ' md:col-start-2 lg:col-start-3' : '' ) : ''"
          >
            <div :class="[
              'glass-bg px-8 flex flex-col',
              !mobile ? 'login-center min-h-[640px]': 'h-full', 
              notebook && !mobile && 'transform-login-center'
            ]">
              <router-view v-slot="{ Component }" class="grow">
                <Transition
                  appear
                  mode="out-in"
                  enter-active-class="transition ease-out duration-150 transform"
                  enter-from-class="opacity-0 translate-x-8"
                  leave-active-class="ease-in duration-200"
                  leave-to-class="opacity-0 -translate-x-8"
                >
                  <component :is="Component" />
                </Transition>
              </router-view>

              <!-- Support Link -->
              <div
                v-if="!systemStore.getConfig.hideSupport"
                class="flex py-2 flex-wrap items-center gap-1 text-sm justify-center justify-self-center"
              >
                {{ $t('login.helpNeeded') }}
                <a href="#" class="font-semibold" @click.prevent="contactSupport">
                  {{ $t('login.talkToUs') }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!--
        Não exibir temporariamente, até implementaão de condição dinamica via contrato/parametrização
        <div v-if="false" class="my-5 flex items-center gap-1 text-[#666666] dark:text-[#D2D2D2] lg:my-10">
          uma solução <LogoAliare />
        </div>
      -->
    </div>

    <CookiesDialog />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watchEffect } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import useSystemStore from '@/stores/systemStore'
import { arrRand } from '@/common/util'

import UiButton from '@/components/ui/button.vue'
import UiIcon from '@/components/ui/icon.vue'
import UiLangSelector from '@/components/ui/langSelector.vue'
import UiLoader from '@/components/ui/loader.vue'
import CookiesDialog from './components/cookies.vue'
import LoginCarousel from './components/carousel.vue'

import type { Carousel } from '../configuracao/personalizar/components/login/types'

const LoginBackground = defineAsyncComponent(() => import('./components/loginBackground.vue'))

const systemStore = useSystemStore()

const breakpoints = useBreakpoints(breakpointsTailwind)

const preload = ref(true)

const mobile = computed(() => breakpoints.smaller('md').value)
const notebook = computed(() => breakpoints.smaller('2xl').value)

const isLeft = computed(() => systemStore.clientSettings.settings.login.align === 'left')
const isCenter = computed(() => systemStore.clientSettings.settings.login.align === 'center')

const bgImage = computed(() => {
  if (systemStore.getConfig.login?.backgrounds?.length > 0) return arrRand(systemStore.getConfig.login.backgrounds)[0]
  return false
})

const carousels = computed<Carousel[]>(() => systemStore.getConfig.login.carousels)

const blockTheme = computed(() => {
  return systemStore.clientSettings?.settings?.theme?.defaultTheme !== 'DefinidoPeloUsuario'
})

const contactSupport = () => {
  (document.getElementsByClassName('md-chat-widget-btn-wrapper')[0] as HTMLDivElement)?.click()
}

watchEffect(() => {
  if (systemStore.clientSettings.status === 'complete') {
    setTimeout(() => {
      preload.value = false
    }, 400)
  }
})
</script>

<style lang="scss" scoped>
.login-bg {
  background-image: v-bind(bgImage);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
}

.transition-gradient {
  background: linear-gradient(to right, transparent 0, #111 33%, #111 33%, #111 66%, #111 66%, transparent 100%);
  position: absolute;
  top: 0;
  left: -100vw;
  width: 300vw;
  height: 100%;
  z-index: 10;
}

.login-center {
  position: absolute;
  top: 35%;
  left: 50%;
  transform: translate(-50%, -35%);
  max-width: 480px;
  width: calc(100% - 40px);
  padding: 2rem;
  border-radius: 1rem;
  @apply border-white/10 dark:border-[#FDFDFD1F];
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.glass-bg {
  @apply bg-white/40 dark:bg-black/40 backdrop-blur-lg;
}

.transform-login-center {
  transform: translate(-50%, -35%) scale(0.85);
  transition: transform 0.5s ease-in-out;
}
</style>